// `tablet` raised from 768 -> 1024 (spec 109): below 1024 px the persistent
// LeftNavRail is hidden and navigation moves into a hamburger-triggered
// OverlayDrawer. Every existing `mq.tablet` consumer accepts the wider cutover
// (see spec 109 PR audit notes for T011).
export const BREAKPOINTS = {
  mobile: 480,
  smallTablet: 600,
  tablet: 800,
  smallDesktop: 1024,
  desktop: 1200,
  wide: 1500,
  ultraWide: 1800,
} as const;

export const mq = {
  mobile: `@media (max-width: ${BREAKPOINTS.mobile}px)`,
  smallTablet: `@media (max-width: ${BREAKPOINTS.smallTablet}px)`,
  tablet: `@media (max-width: ${BREAKPOINTS.tablet}px)`,
  smallDesktop: `@media (max-width: ${BREAKPOINTS.smallDesktop}px)`,
  desktop: `@media (max-width: ${BREAKPOINTS.desktop}px)`,
  wide: `@media (max-width: ${BREAKPOINTS.wide}px)`,
  wideUp: `@media (min-width: ${BREAKPOINTS.wide}px)`,
  ultraWideUp: `@media (min-width: ${BREAKPOINTS.ultraWide}px)`,
  /**
   * Targets extreme browser-zoom levels (≥ 400 % on a 1080p screen) where
   * fixed-position chrome (footer, floating action bars) would otherwise
   * cover most of the viewport and hide scrollable content.
   * At 400 % zoom on 1080px height → effective height ≈ 270px ≈ 16.9rem.
   * 20rem (320px) provides a comfortable threshold.
   */
  highZoom: '@media (max-height: 20rem)',
} as const;

/**
 * Base font-size (px) for each screen-width tier.
 * Every component uses `rem` via `fontScale` / `iconSize`, so changing a
 * value here rescales the entire UI (including Fluent button sizes) for
 * that breakpoint automatically.
 *
 * In practice only `mobile` (min) and `ultraWide` (max) are consumed by
 * `AppStyles`, which interpolates smoothly between them with `clamp()`.
 * The intermediate tiers document the resulting size at that viewport.
 *
 * Desktop major text lands at 16px (FR: "desktop major text should be
 * 16px"), and smaller viewports scale proportionally smaller.
 */
export const BASE_FONT_SIZE = {
  mobile: 12,
  smallTablet: 13,
  tablet: 13,
  smallDesktop: 14,
  desktop: 15, // major text reference — also the basis for pxToRem()
  wide: 16,
  ultraWide: 18,
} as const;

/** Reference size used by `pxToRem()` — must match `BASE_FONT_SIZE.desktop`. */
export const APP_BASE_FONT_SIZE_PX = BASE_FONT_SIZE.desktop;

export const APP_FONT_FAMILY =
  "'Segoe UI', 'Segoe UI Web (West European)', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif";

const pxToRem = (px: number): string => `${px / APP_BASE_FONT_SIZE_PX}rem`;

/**
 * Standardised font-scale — exactly 6 rem values used across the entire app.
 *
 * | Token       | Value    | px at 16px base | Purpose                        |
 * |-------------|----------|-----------------|--------------------------------|
 * | caption     | 0.75rem  | 12px            | Badges, pills, helper text     |
 * | body        | 0.875rem | 14px            | Default body / form text       |
 * | subtitle    | 1rem     | 16px            | Sub-headings, emphasis         |
 * | title       | 1.2rem   | 19.2px          | Card titles, section labels    |
 * | subheading  | 1.4rem   | 22.4px          | Page titles, dashboard heading |
 * | heading     | 1.8rem   | 28.8px          | Hero banners, large display    |
 *
 * Usage: `fontSize: fontScale.body`
 */
export const fontScale = {
  chartLabel: '0.6rem',
  caption: '0.75rem',
  body: '0.875rem',
  subtitle: '1rem',
  cardtitle: '1.125rem',
  title: '1.2rem',
  subheading: '1.4rem',
  heading: '1.85rem',
} as const;

/** Convert a design-px icon size to a rem string for Fluent icon `style` props. */
export const iconSize = (px: number): string => pxToRem(px);

/**
 * Fluid spacing tokens using CSS `clamp()`.
 * Scale smoothly between a minimum and maximum value.
 *
 * Usage: `marginBottom: fluidSpace.md`
 */
export const fluidSpace = {
  /** 4px → 8px */
  xxs: 'clamp(4px, 0.3vw, 8px)',
  /** 6px → 10px */
  xs: 'clamp(6px, 0.45vw, 10px)',
  /** 8px → 14px */
  sm: 'clamp(8px, 0.6vw, 14px)',
  /** 10px → 16px */
  md: 'clamp(10px, 0.7vw, 16px)',
  /** 12px → 18px */
  lg: 'clamp(12px, 0.9vw, 18px)',
  /** 16px → 24px */
  xl: 'clamp(16px, 1.2vw, 24px)',
  /** 24px → 40px */
  xxl: 'clamp(24px, 1.8vw, 40px)',
  /** 40px → 64px */
  xxxl: 'clamp(40px, 3vw, 64px)',
} as const;

/**
 * Fluid container heights for hero/banner sections.
 */
export const fluidHeight = {
  /** 200px → 300px */
  bannerSm: 'clamp(200px, 15vw, 300px)',
  /** 280px → 347px */
  bannerMd: 'clamp(280px, 30vw, 347px)',
  /** 347px → 480px */
  bannerLg: 'clamp(347px, 22vw, 480px)',
} as const;
